import React, { useState, useRef, useEffect } from 'react';

const ResultField = ({ title, value, fieldId, onValueChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value || '');
  const [isOverflowing, setIsOverflowing] = useState(false);
  const valueRef = useRef(null);

  useEffect(() => {
    if (valueRef.current) {
      const isTextOverflowing =
        valueRef.current.scrollWidth > valueRef.current.clientWidth;
      setIsOverflowing(isTextOverflowing);
    }
  }, [editedValue]);

  // Update editedValue when value prop changes
  useEffect(() => {
    setEditedValue(value || '');
  }, [value]);

  if (!value && fieldId === 'error') return null;

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = (e) => {
    // Stop event propagation to prevent triggering edit mode again
    e.preventDefault();
    e.stopPropagation();

    setIsEditing(false);
    if (editedValue !== value && onValueChange) {
      onValueChange(fieldId || title, editedValue);
    }
  };

  const handleChange = (e) => {
    setEditedValue(e.target.value);
  };

  const handleBlur = (e) => {
    // If the click is on the check icon, let the check icon handler handle it
    if (e.relatedTarget && e.relatedTarget.classList.contains('check-icon')) {
      return;
    }

    setIsEditing(false);
    if (editedValue !== value && onValueChange) {
      onValueChange(fieldId || title, editedValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      if (editedValue !== value && onValueChange) {
        onValueChange(fieldId || title, editedValue);
      }
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditedValue(value || ''); // Reset to original value
    }
  };

  return (
    <div
      className="result-container"
      onClick={(e) => {
        // Prevent any click on the container from triggering unwanted effects
        e.stopPropagation();
      }}
    >
      <div className="result-field">
        <div className="result-field-title">
          <strong>{title}</strong>
        </div>

        {isEditing ? (
          <div className="result-field-value-container">
            <div
              className="result-field-value-wrapper editing-wrapper"
              style={{ width: '100%' }}
            >
              <input
                type="text"
                value={editedValue}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                className="result-field-input result-field-input-editing"
                style={{
                  width: '100%',
                  display: 'block',
                }}
              />
            </div>
          </div>
        ) : (
          <div className="result-field-value-container">
            <div className="result-field-value-wrapper">
              <div
                ref={valueRef}
                className={`result-field-value ${
                  isOverflowing ? 'overflow' : ''
                }`}
                onDoubleClick={handleDoubleClick}
              >
                <span>{editedValue}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{ width: '40px', height: '40px', position: 'relative' }}>
        {isEditing ? (
          <button
            className="check-icon"
            onClick={handleSaveClick}
            aria-label="Save"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSaveClick(e);
              }
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        ) : (
          <button
            className="edit-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick();
            }}
            aria-label="Edit"
            id={`edit-${fieldId || title.replace(/\s+/g, '-').toLowerCase()}`}
            style={{
              visibility: isEditing ? 'hidden' : 'visible',
              opacity: isEditing ? 0 : 1,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ResultField;
